import { useEffect, useState } from 'react';
import { CheckIcon } from '@phosphor-icons/react';
import { ResponsiveDrawer } from '@/components/ui/responsive-drawer';
import { DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from '@/components/ui/drawer';
import { cn } from '@/lib/utils';
import { useRecurringTaxonomyColors } from '@/components/recurring-payments/taxonomy';

export interface MarkRecurringOptions {
  kind: 'Subscription' | 'Utility';
  cadence: 'Monthly' | 'Yearly';
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  merchant: string | null;
  /** Seeded by funding source: credit card => Assinatura, débito => Conta fixa. */
  defaultKind: 'Subscription' | 'Utility';
  saving?: boolean;
  onConfirm: (options: MarkRecurringOptions) => void;
}

/** Compact chooser for the manual "marcar como recorrente" flow: tipo + frequência. */
export function MarkRecurringSheet({ open, onOpenChange, merchant, defaultKind, saving = false, onConfirm }: Props) {
  const taxonomyColors = useRecurringTaxonomyColors();
  const [kind, setKind] = useState<MarkRecurringOptions['kind']>(defaultKind);
  const [cadence, setCadence] = useState<MarkRecurringOptions['cadence']>('Monthly');

  // Re-seed the choices whenever the sheet opens for a (new) transaction.
  useEffect(() => {
    if (open) {
      setKind(defaultKind);
      setCadence('Monthly');
    }
  }, [open, defaultKind]);

  return (
    <ResponsiveDrawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent
        className="px-0 sm:max-w-md"
        style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
      >
        <DrawerHeader className="border-b border-border-subtle px-5 py-4 text-left">
          <DrawerTitle className="text-lg font-bold">Marcar como recorrente</DrawerTitle>
          <DrawerDescription className="truncate">{merchant ?? 'Transação selecionada'}</DrawerDescription>
        </DrawerHeader>

        <div className="p-5">
          <p className="mb-2 text-xs text-text-tertiary">Tipo</p>
          <div className="mb-4 grid grid-cols-2 gap-1 rounded-xl border border-border-subtle bg-surface-sunken p-1">
            {([
              { key: 'Subscription', label: 'Assinatura', color: taxonomyColors.assinaturas },
              { key: 'Utility', label: 'Conta fixa', color: taxonomyColors.fixas },
            ] as const).map((option) => (
              <button
                key={option.key}
                type="button"
                onClick={() => setKind(option.key)}
                className={cn(
                  'flex min-h-9 items-center justify-center gap-1.5 rounded-lg text-sm font-medium transition-colors',
                  kind === option.key ? 'bg-surface-raised text-text-primary shadow-sm' : 'text-text-secondary',
                )}
              >
                <span className="size-2 rounded-full" style={{ backgroundColor: option.color }} />
                {option.label}
              </button>
            ))}
          </div>

          <p className="mb-2 text-xs text-text-tertiary">Frequência</p>
          <div className="mb-5 grid grid-cols-2 gap-1 rounded-xl border border-border-subtle bg-surface-sunken p-1">
            {([
              { key: 'Monthly', label: 'Mensal' },
              { key: 'Yearly', label: 'Anual' },
            ] as const).map((option) => (
              <button
                key={option.key}
                type="button"
                onClick={() => setCadence(option.key)}
                className={cn(
                  'flex min-h-9 items-center justify-center rounded-lg text-sm font-medium transition-colors',
                  cadence === option.key ? 'bg-surface-raised text-text-primary shadow-sm' : 'text-text-secondary',
                )}
              >
                {option.label}
              </button>
            ))}
          </div>

          <button
            type="button"
            disabled={saving}
            onClick={() => onConfirm({ kind, cadence })}
            className="flex min-h-12 w-full items-center justify-center gap-2 rounded-xl bg-primary text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary-hover disabled:opacity-60"
          >
            <CheckIcon size={17} weight="bold" /> Marcar recorrência
          </button>
        </div>
      </DrawerContent>
    </ResponsiveDrawer>
  );
}
